import {Quiz, QuizData} from "types/Quiz";
import client from "services/ngage/api-client";
import DataPage from "types/DataPage";
import deserializeQuiz from "services/ngage/deserializers/quiz";
import {queryString} from "services/string";
import DataResource from "../../types/DataResource";
import {snakeKeys} from "../array";

export type QuizFilters = {
  search?: string | null
  quizzableType?: 'exhibit'
  quizzableId?: number
  page?: number
  perPage?: number
  sites?: string
}

/**
 * Get all quizzes (paginated)
 */
async function all(params: QuizFilters) {
  const query = queryString({
    quizzable_type: params.quizzableType,
    quizzable_id: params.quizzableId,
    page: params.page,
    per_page: params.perPage,
  })

  const response = await client.get<DataPage<Quiz> | null>(`/quizzes?${query}`)

  if (response.data) {
    response.data.data = response.data.data.map(deserializeQuiz)
  }

  return response.data
}

/**
 * Get all quizzes (paginated)
 */
async function filtered(filters: QuizFilters = {}) {
  const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))
  const response = await client.get<DataResource<Quiz>>('/quizzes', {params})

  if (response.data) {
    response.data.data = response.data.data.map(deserializeQuiz)
  }

  return response.data
}

/**
 * Get a single quiz
 *
 * @param {number} id
 */
async function get(id: number) {
  const response = await client.get<Quiz>(`/quizzes/${id}`)

  return response.data ? deserializeQuiz(response.data) : null
}

/**
 * Store a new quiz
 *
 * @param data
 */
async function store(data: QuizData) {
  const response = await client.post<Quiz>('/quizzes', data)

  return response.data ? deserializeQuiz(response.data) : null
}

/**
 * Update an existing quiz
 *
 * @param {number} id
 * @param data
 */
async function update(id: number, data: QuizData) {
  const response = await client.put<Quiz>(`/quizzes/${id}`, data)

  return response.data ? deserializeQuiz(response.data) : null
}

/**
 * Permanently delete a quiz
 *
 * @param {number} id
 */
async function destroy(id: number) {
  const response = await client.delete<null>(`/quizzes/${id}`)

  return response.status === 204
}

const quizzes = {
  all,
  filtered,
  get,
  store,
  update,
  destroy
}

export default quizzes;